import { captureImage } from './actions/captureImage';
import { setCamera } from './actions/setCamera';
import { setSceneFromId, setSceneFromJson } from './actions/setScene';
import type { TSceneJsonDto } from './domain/scene/sceneJsonDto';
import { store } from './store/appStore';
import { setResolution } from './store/appSlice';
import type { TCamera } from './types/protocolTypes';
import { waitForAppReady, waitForSceneReady } from './appContext';
import { profileSync, profileAsync, setProfilerEnabled } from './profiler';

/**
 * Promise queue for waiting for render completion.
 * When a render completes (onUpdate fires), the next pending promise is resolved.
 * We use a sequence number to track when promises were added vs when renders complete.
 */
type PendingRender = {
  resolve: () => void;
  sequence: number;
};

const renderPromiseQueue: PendingRender[] = [];
let nextSequence = 0;

/**
 * Called by the viewer's onUpdate callback when rendering completes.
 * Resolves the next pending waitForRender promise.
 */
export const notifyRenderComplete = (): void => {
  // Resolve the next pending promise in the queue (FIFO)
  // This ensures we resolve promises in the order they were added
  if (renderPromiseQueue.length > 0) {
    const pending = renderPromiseQueue.shift();
    if (pending) {
      pending.resolve();
    }
  }
};

interface CaptureImageOptions {
  type?: string;
  quality?: number;
  uploadURL?: string;
}

interface SequenceCommand {
  method: string;
  args?: unknown[];
}

interface WindowEx extends Window {
  captureImage: (options?: CaptureImageOptions) => Promise<ArrayBuffer | boolean>;
  setCamera: (camera: TCamera) => void;
  setResolution: (width: number, height: number) => void;
  setSceneFromId: (id: string) => void;
  setSceneFromJson: (scene: TSceneJsonDto) => void;
  waitForAppReady: () => Promise<void>;
  waitForSceneReady: () => Promise<void>;
  waitForRender: () => Promise<void>;
  // Sequence execution
  executeSequence: (commands: SequenceCommand[]) => Promise<unknown[]>;
  // Profiler methods
  setProfilerEnabled: (enabled: boolean) => void;
}

declare let window: WindowEx;

/**
 * Expose functions to control the application from global window scope.
 */
export const init = () => {
  /**
   * Capture image.
   * @param options Options object with type, quality, and optional uploadURL.
   */
  window.captureImage = profileAsync(
    'captureImage',
    async (options?: CaptureImageOptions): Promise<ArrayBuffer | boolean> => {
      const viewerRef = store.getState().appRefs.viewerRef;

      if (!viewerRef?.current) {
        return false;
      }

      const opts = options || {};

      const blob = await captureImage(viewerRef.current, opts.type, opts.quality);

      if (!blob) {
        return false;
      }

      const arrayBuffer = await blob.arrayBuffer();

      // If uploadURL is provided, upload the image
      if (opts.uploadURL) {
        const response = await fetch(opts.uploadURL, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/octet-stream',
          },
          body: arrayBuffer,
        });

        if (!response || !response.ok) {
          throw new Error(`Failed to upload image: ${response?.statusText || 'Unknown error'}`);
        }

        return true;
      }

      return arrayBuffer;
    },
  );

  /**
   * Set camera from camera desc.
   * @param camera Camera descriptor.
   */
  window.setCamera = profileSync('setCamera', (camera: TCamera) => {
    const viewerRef = store.getState().appRefs.viewerRef;

    if (!viewerRef?.current) {
      return;
    }

    setCamera(viewerRef.current, camera);
  });

  /**
   * Set resolution.
   * @param width Width.
   * @param height Height.
   */
  window.setResolution = profileSync('setResolution', (width: number, height: number) => {
    store.dispatch(setResolution({ x: width, y: height }));
    // Note: Resolution change triggers React re-render, which will call viewer.setSize()
    // This happens asynchronously, so waitForRender() should be called after this
  });

  /**
   * Set scene from ID.
   * @param id Scene ID.
   */
  window.setSceneFromId = profileAsync('setSceneFromId', async (id: string) => {
    await setSceneFromId(store.dispatch, id);
  });

  /**
   * Set scene from JSON.
   * @param scene Scene JSON.
   */
  window.setSceneFromJson = profileAsync('setSceneFromJson', async (scene: TSceneJsonDto) => {
    await setSceneFromJson(store.dispatch, scene);
  });

  /**
   * Returns a promise that resolves when the app is ready.
   */
  window.waitForAppReady = profileAsync('waitForAppReady', async () => {
    await waitForAppReady();
  });

  /**
   * Returns a promise that resolves when the scene is ready.
   */
  window.waitForSceneReady = profileAsync('waitForSceneReady', async () => {
    await waitForSceneReady();
  });

  /**
   * Wait for the next render to complete.
   * Call this after setCamera or setResolution to ensure the frame is painted before capturing.
   * This waits for the viewer's onUpdate callback, which fires after rendering is complete.
   *
   * For resolution changes, this also waits for React to finish re-rendering.
   */
  window.waitForRender = profileAsync('waitForRender', (): Promise<void> => {
    const viewerRef = store.getState().appRefs.viewerRef;

    // Ensure the viewer exists
    if (!viewerRef?.current) {
      return Promise.resolve();
    }

    const viewer = viewerRef.current;
    const sequence = nextSequence++;

    // For resolution changes, we need to wait for React to finish re-rendering
    // The resolution change triggers a React re-render, which then calls viewer.setSize()
    // We use requestAnimationFrame to ensure React has finished its render cycle
    return new Promise<void>(resolve => {
      // Wait for React to finish re-rendering (if resolution changed)
      // This ensures the Container component has updated
      requestAnimationFrame(() => {
        // Wait one more frame to ensure ResizeObserver has fired and viewer.setSize() has been called
        // This is necessary because ResizeObserver callbacks fire asynchronously
        requestAnimationFrame(() => {
          // Ensure a render is triggered by setting needsUpdate
          // This handles cases where setCamera/setResolution might not have triggered a render yet
          if (viewer.needsUpdate !== undefined) {
            viewer.needsUpdate = true;
          }

          // Add promise to queue with sequence number
          // This will be resolved when onUpdate fires after the next render
          renderPromiseQueue.push({
            resolve,
            sequence,
          });
        });
      });
    });
  });

  /**
   * Execute a sequence of window methods sequentially.
   * @param commands Array of commands with method name and arguments.
   * @returns Array of results from each method execution.
   */
  window.executeSequence = profileAsync(
    'executeSequence',
    async (commands: SequenceCommand[]): Promise<unknown[]> => {
      const results: unknown[] = [];

      for (const command of commands) {
        const method = window[command.method as keyof WindowEx];

        if (typeof method !== 'function') {
          throw new TypeError(`Method "${command.method}" is not a valid window method`);
        }

        const args = command.args ?? [];
        const result = await method(...args);
        results.push(result);
      }

      return results;
    },
  );

  /**
   * Enable or disable the profiler.
   */
  window.setProfilerEnabled = setProfilerEnabled;
};
